﻿/* https://brandplatform.smartphoto.com/general/molecules/my-account/ */
import { createClickableElement } from "../atoms/button";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { IIconResult } from "../../utils/icons";
import { calculateAndSetHeightOfMobileNavigationLevel } from "../../utils/mobile-menu";
import { createDropDownKeyboardNavigator } from "../technical/keyboard-navigator";

export interface IInitMyAccountDependencies {
	readonly getIconsAsync: (ids: string[]) => Promise<IIconResult[]>;
	readonly onOpen?: () => void;
	readonly onClose?: () => void;
	readonly isMobile: () => boolean;
	readonly loadAccountMenu: () => Promise<string>;
}

export function initMyAccount(mainNavigationEl: HTMLElement, deps: IInitMyAccountDependencies) {
	const { getIconsAsync, onClose, onOpen, isMobile, loadAccountMenu } = deps;
	let onDocumentClickDisposable: IDisposable;
	let keyboardNavigatorDisposables: IDisposable;
	const icons = {};
	let loaded = false;
	const myAccountContainerEl = expressQuerySelector<HTMLElement>(mainNavigationEl, ".o-icon-navigation__user", true);
	const buttonEl = expressQuerySelector<HTMLElement>(myAccountContainerEl, ".o-icon-navigation__user-icon", true);
	const menuEl = expressQuerySelector<HTMLElement>(myAccountContainerEl, ".o-icon-navigation__user-menu", true);
	const overLayEl = expressQuerySelector<HTMLElement>(menuEl, ".technical-navigation-overlay", true);
	const contentEl = expressQuerySelector<HTMLElement>(overLayEl, ".m-navigation-overlay__content", true);
	const closeButtonEl = expressQuerySelector<HTMLElement>(overLayEl, ".m-navigation-overlay__close-container", true);

	const setOverLayClass = () => {
		overLayEl.classList.remove(...["m-navigation-overlay__mobile-view", "m-navigation-overlay__desktop-view"]);
		overLayEl.classList.add(isMobile() ? "m-navigation-overlay__mobile-view" : "m-navigation-overlay__desktop-view");
	};

	const onOpenMyAccount = async () => {
		if (!loaded) {
			const res = await loadAccountMenu();
			onOpen && onOpen();
			contentEl.insertAdjacentHTML("beforeend", res);
			const buttonEls = expressQuerySelectorAll<HTMLElement>(contentEl, ".technical-hassvg").filter(
				(el) => !!el.getAttribute("data-icon-svg"),
			);
			const urls = buttonEls
				.map((el) => el.getAttribute("data-icon-svg")!)
				.filter((value, index, self) => self.indexOf(value) === index);
			getIconsAsync(urls).then((res) => {
				res.forEach((icon) => (icons[icon.url] = icon.svg));
				buttonEls.forEach((el) => el.insertAdjacentHTML("beforeend", icons[el.getAttribute("data-icon-svg")!]));
			});
			const logoutButton = expressQuerySelector<HTMLAnchorElement>(contentEl, 'a[href*="logout"]', false);
			expressEventListener(logoutButton, "click", (e) => {
				e.preventDefault();
				window.loginWatcher && window.loginWatcher.cancel();
				window.location.href = logoutButton.href;
			});
			if (!isMobile()) {
				keyboardNavigatorDisposables.dispose();
				keyboardNavigatorDisposables.add(
					createDropDownKeyboardNavigator(myAccountContainerEl, {
						onEnter: onButtonClick,
						onEscape: onCloseIconClick,
						onBeforeArrowDown: () => {
							if (!menuEl.classList.contains("menu-is-open")) openMenu();
						},
						dropdownItemEls: expressQuerySelectorAll<HTMLElement>(contentEl, ".m-navigation-mobile-ui"),
					}),
				);
			}
			loaded = true;
		} else {
			onOpen && onOpen();
			return Promise.resolve();
		}
	};

	const closeMenu = () => {
		onClose && onClose();
		menuEl.classList.remove("menu-is-open");
		detachDocumentClick();
		return Promise.resolve();
	};

	const openMenu = async () => {
		setOverLayClass();
		await onOpenMyAccount();
		menuEl.classList.add("menu-is-open");
		if (isMobile()) {
			const menuButtonList = expressQuerySelector<HTMLElement>(menuEl, ".o-my-account__list .a-button", true);
			calculateAndSetHeightOfMobileNavigationLevel(menuButtonList);
		}
		attachOnDocumentClick();
	};

	const onCloseIconClick = (e: Event) => {
		e.stopPropagation();
		closeMenu();
	};

	const onButtonClick = () => {
		return menuEl.classList.contains("menu-is-open") ? closeMenu() : openMenu();
	};

	const onDocumentClick = (e: MouseEvent) => {
		if (!(e.target as HTMLElement).closest(".o-icon-navigation__user-menu, .o-icon-navigation__user-icon")) {
			closeMenu();
		}
	};

	const button = createClickableElement(buttonEl, onButtonClick);
	const eventDisposables = compositeDisposable([
		expressEventListener(closeButtonEl, "click", onCloseIconClick),
		expressEventListener(window, "resize", setOverLayClass),
	]);

	if (!isMobile()) {
		keyboardNavigatorDisposables = compositeDisposable([
			createDropDownKeyboardNavigator(myAccountContainerEl, {
				onEnter: onButtonClick,
				onEscape: onCloseIconClick,
				onBeforeArrowDown: () => {
					if (!menuEl.classList.contains("menu-is-open")) openMenu();
				},
				dropdownItemEls: [],
			}),
		]);
	}

	const attachOnDocumentClick = () =>
		(onDocumentClickDisposable = expressEventListener(document, "click", onDocumentClick, true));

	const detachDocumentClick = () => onDocumentClickDisposable && onDocumentClickDisposable.dispose();

	return {
		dispose: () => {
			eventDisposables.dispose();
			detachDocumentClick();
			button.dispose();
			closeMenu();
			keyboardNavigatorDisposables && keyboardNavigatorDisposables.dispose();
		},
	};
}
